import { Injectable, Injector } from '@angular/core';
import { map, catchError } from 'rxjs/operators';
import { CheckinHttpClient } from 'src/app/shared/services/http/http-client.service';
import { EnvService } from 'src/app/shared/common/services/env.service';
import { Observable } from 'rxjs';
import { ConfigService } from 'src/app/shared/services/config/config.service';
import { InjectorService } from 'src/app/shared/services/injector/injector.service';
import { GuestForm, GuestControl } from '../../models/form/wizard-form.model';
import { ControlsConfig } from '../../models/form/controls.model';
import { EControlsType, EControls } from '../../enums/controls.enum';

@Injectable({ providedIn: 'root' })
export class FormService {
  private http: CheckinHttpClient;
  private url: string;
  private config: ConfigService;

  issuedAtEntityByCountry = {
    PT: 'country'
  };

  issuedAtLabelByCountry = {
    PT: 'country-issued-in'
  };

  issuedAtTypeByCountry = {
    PT: EControlsType.SELECT
  };

  constructor() {
    const injector: Injector = InjectorService.get();

    this.config = injector.get(ConfigService);
    const env: EnvService = injector.get(EnvService);

    Object.assign(this, {
      http: injector.get(CheckinHttpClient),
      url: `${env.endpoint}/v2/config/checkin/form/${this.config.checkin.hash}`
    });
  }

  public get(guestId: number): Observable<GuestForm['list']> {
    return this.http.get(`${this.url}/${guestId}`).pipe(
      map((res: { form: GuestControl[] }) => this.sanitize(res.form)),
      catchError((error: any) => { throw error; })
    );
  }

  public update(guestId: number, controls: { [key: string]: any }): Observable<GuestForm['list'] | boolean> {
    return this.http.put(`${this.url}/${guestId}`, controls).pipe(
      map((res: { form: GuestControl[], ok: boolean }) => res.form ? this.sanitize(res.form) : res.ok),
      catchError((error: any) => { throw error; })
    );
  }

  public close(guestId: number, params: { [key: string]: any }): Observable<boolean> {
    return this.http.patch(`${this.url}/${guestId}`, params).pipe(
      map((res: { ok: boolean }) => res.ok),
      catchError((error: any) => { throw error; })
    );
  }

  public sanitize(controls: GuestControl[]): GuestForm['list'] {
    if (!Array.isArray(controls) || controls.length === 0) {
      return {} as any;
    }

    return controls.reduce((acc, { id, value, required, order, ...c }) => {
      const type: EControlsType = ControlsConfig[id].type;

      if (type === EControlsType.AUTOSUGGEST) {
        if (!value || (typeof value === 'object' && !value.hasOwnProperty('value'))) {
          value = {
            value: ''
          };
        }
      }

      if (type === EControlsType.DATE && value) {
        value = new Date(value);
      }

      if (id === EControls.clientNationality) {
        value = typeof value !== 'number' ? '' : value;
      }

      if (id === EControls.clientIdIssuedAt && this.config.booking.acc.country.code === 'PT') {
        ControlsConfig[id].entity = this.issuedAtEntityByCountry[this.config.booking.acc.country.code] || 'city';
        ControlsConfig[id].label = this.issuedAtLabelByCountry[this.config.booking.acc.country.code] || 'issued-in';
        ControlsConfig[id].type = this.issuedAtTypeByCountry[this.config.booking.acc.country.code] || EControlsType.AUTOSUGGEST;
        ControlsConfig[id].service = 'countries';
      }

      return { ...acc, [id]: {
        ...ControlsConfig[id],
        ...c,
        id,
        value,
        required,
        order
      } };
    }, {});
  }
}
